import { useIntl } from '@/intl'

import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'

import { routesSelector } from '@/scopes/prices/components/selectors'
import { CollapseByHeight } from '@/scopes/prices/components/TransportSelector/CollapseByHeight'
import { Column } from '@/scopes/prices/components/TransportSelector/Column'

import { ArrivalTitle, DepartureTitle } from '@/scopes/prices/components/TransportSelector/FlightSelector/Headers'
import { createFlightComponentInteractionEvent } from '@/scopes/prices/domain/analytics'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { space, SpaceBetween, tok } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'
import { FlightCard } from './FlightCard'

const Wrapper = styled.div`
  display: flex;
  width: 100%;

  ${Column}:first-child {
    margin-right: ${space('primitives_space_16')};
  }
`

const FlightSelectorTitle = styled(Typography)`
    display: block;
    margin-bottom: ${space('primitives_space_28')};
`

const FlightSelectorContainer = styled.div`
    padding: ${space('primitives_space_24')};
    border: 2px solid ${tok('primitives_color_neutral_100')};
    border-radius: 12px;
`
export function FlightSelectorDesktop() {
  const intl = useIntl()
  const { inboundRoutes, outboundRoutes } = usePricesSelector(routesSelector)
  const { sendEvent } = useAnalytics()

  if (isEmpty(outboundRoutes) || isEmpty(inboundRoutes))
    return null

  function handleCollapseToggle(result: string) {
    sendEvent(createFlightComponentInteractionEvent({ type: 'outbound', result }))
  }

  return (
    <>
      {outboundRoutes.length < 3
        ? (
            <FlightSelector />
          )
        : (
            <CollapseByHeight
              openText={intl.formatMessage({ id: 'prices_transport_selector_flight-selector-show-less' })}
              closeText={intl.formatMessage({ id: 'prices_transport_selector_flight-selector-show-more' })}
              height="370px"
              onClick={handleCollapseToggle}
            >
              <FlightSelector />
            </CollapseByHeight>
          )}
    </>
  )
}

function FlightSelector() {
  const {
    setInboundRouteId,
    setOutboundRouteId,
  } = React.useContext(PricesUseCasesContext)
  const intl = useIntl()
  const {
    inboundRoutes,
    outboundRoutes,
    selectedOutboundRouteId,
    selectedInboundRouteId,
  } = usePricesSelector(routesSelector)

  return (
    <FlightSelectorContainer>
      <FlightSelectorTitle $variant="display-f-regular">
        {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_title' })}
      </FlightSelectorTitle>
      <Wrapper id="FlightSelector">
        <Column value={selectedOutboundRouteId as string}>
          <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
            <DepartureTitle />
            {outboundRoutes.map((item, index) => (
              <FlightCard
                key={index}
                {...item}
                setSelected={setOutboundRouteId}
                isSelected={selectedOutboundRouteId === item.id}
              />
            ))}
          </SpaceBetween>
        </Column>
        <Column value={selectedInboundRouteId as string}>
          <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
            <ArrivalTitle />
            {inboundRoutes.map((item, index) => (
              <FlightCard
                key={index}
                {...item}
                setSelected={setInboundRouteId}
                isSelected={selectedInboundRouteId === item.id}
              />
            ))}
          </SpaceBetween>
        </Column>
      </Wrapper>
    </FlightSelectorContainer>
  )
}
